<template>
  <div class="callback-container">
    <div class="callback-card">
      <!-- 加载状态 -->
      <div v-if="state.loading" class="loading-state">
        <div class="spinner">🔄</div>
        <h2>正在处理授权...</h2>
        <p>请稍候，我们正在验证您的 TikTok 授权信息</p>
      </div>

      <!-- 成功状态 -->
      <div v-else-if="!getters.hasError.value && getters.isAuthenticated.value" class="success-state">
        <div class="success-icon">✅</div>
        <h2>登录成功！</h2>
        <p>欢迎 {{ getters.userDisplayName.value }}，正在跳转到用户面板...</p>
        <div class="user-preview">
          <img 
            v-if="getters.userAvatar.value" 
            :src="getters.userAvatar.value" 
            :alt="getters.userDisplayName.value"
            class="preview-avatar"
          >
          <div class="preview-info">
            <span class="preview-name">{{ getters.userDisplayName.value }}</span>
            <span class="preview-id">{{ state.user?.open_id }}</span>
          </div>
        </div>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="getters.hasError.value" class="error-state">
        <div class="error-icon">❌</div>
        <h2>授权失败</h2>
        <div class="error-details">
          <p>{{ state.error }}</p>
        </div>
        <div class="error-actions">
          <router-link to="/login" class="btn btn-primary">
            重新登录
          </router-link>
          <router-link to="/" class="btn btn-outline">
            返回首页
          </router-link>
        </div>
      </div>

      <!-- 默认状态（无参数） -->
      <div v-else class="default-state">
        <div class="warning-icon">⚠️</div>
        <h2>无效的回调</h2>
        <p>未检测到有效的授权参数，请重新发起登录流程。</p>
        <router-link to="/login" class="btn btn-primary">
          前往登录
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../store'

export default {
  name: 'Callback',
  data() {
    return {
      processed: false
    }
  },
  computed: {
    authStore() {
      return useAuthStore()
    },
    state() {
      return this.authStore.state
    },
    getters() {
      return this.authStore.getters
    },
    actions() {
      return this.authStore.actions
    }
  },
  mounted() {
    // 在mounted生命周期函数中处理回调
    this.handleCallback()
  },
  methods: {
    async handleCallback() {
      if (this.processed) return
      this.processed = true
      console.log('=== 开始处理回调 ===')
      console.log('URL参数:', this.$route.query)

      const { code, state: stateParam, error, error_description } = this.$route.query
      
      if (error) {
        console.error('URL中包含错误:', error, error_description)
        this.state.error = `${error}: ${error_description || '未知错误'}`
        return
      }

      // 检查必需参数
      if (!code || !stateParam) {
        console.error('缺少必需参数:', { code: !!code, state: !!stateParam })
        this.state.error = '缺少必需的授权参数'
        return
      }

      console.log('准备调用 API:', { code, state: stateParam })

      try {
        // 处理授权回调
        const success = await this.actions.handleCallback(code, stateParam)
        
        console.log('回调处理结果:', success)
        console.log('当前认证状态:', this.getters.isAuthenticated.value)
        console.log('当前用户信息:', this.state.user)

        if (success) {
          console.log('回调成功，2秒后跳转到dashboard')
          // 延迟跳转到用户面板
          setTimeout(() => {
            console.log('开始跳转到dashboard')
            this.$router.push('/dashboard')
          }, 2000)
        } else {
          console.error('回调处理失败，当前错误:', this.state.error)
        }
      } catch (error) {
        console.error('处理授权回调失败:', error)
        this.state.error = '处理回调时发生异常: ' + error.message
      }
    }
  }
}
</script>

<style scoped>
.callback-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem;
}

.callback-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  padding: 3rem 2rem;
  text-align: center;
}

/* 加载状态 */
.loading-state h2 {
  color: #333;
  margin: 1rem 0 0.5rem;
  font-size: 1.5rem;
}

.loading-state p {
  color: #666;
  line-height: 1.6;
}

.spinner {
  font-size: 3rem;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 成功状态 */
.success-state .success-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.success-state h2 {
  color: #4CAF50;
  margin-bottom: 0.5rem;
  font-size: 1.75rem;
}

.success-state p {
  color: #666;
  margin-bottom: 2rem;
}

.user-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  margin-top: 1.5rem;
}

.preview-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
}

.preview-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.preview-name {
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
}

.preview-id {
  color: #666;
  font-size: 0.9rem;
}

/* 错误状态 */
.error-state .error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-state h2 {
  color: #f44336;
  margin-bottom: 1rem;
  font-size: 1.75rem;
}

.error-details {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 2rem;
}

.error-details p {
  color: #d32f2f;
  margin: 0;
  font-size: 0.95rem;
}

.error-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* 默认状态 */
.default-state .warning-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.default-state h2 {
  color: #ff9800;
  margin-bottom: 1rem;
  font-size: 1.75rem;
}

.default-state p {
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
}

/* 按钮样式 */
.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-size: 1rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .callback-card {
    padding: 2rem 1.5rem;
  }
  
  .error-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .user-preview {
    flex-direction: column;
    text-align: center;
  }
  
  .preview-info {
    align-items: center;
  }
}
</style> 